import Card from "./Card";
import Button from "Components/Common/Button/Button";
import Skeleton from "Components/Common/Skeleton/Skeleton";
import { DrawDetail, fmtMoney } from "../DrawHistory.types";
import { formatDate } from "utils/Common/Date";

interface G702Row {
  num: string;
  label: string;
  value: string;
  tone?: "blue" | "green" | "amber" | "red";
  isTotal?: boolean;
}

interface SummaryTabProps {
  drawDetail: DrawDetail | null;
  loading: boolean;
  onExportG702: () => void;
  exportingG702?: boolean;
}

const SummaryTab = ({
  drawDetail,
  loading,
  onExportG702,
  exportingG702 = false,
}: SummaryTabProps) => {
  // DrawDetail type may not include summary in types; use a safe any-cast to access it if present
  const g702 = (drawDetail as any)?.summary;
  const retainagePct = g702?.retainageRate
    ? parseFloat(g702.retainageRate)
    : null;

  const rows: G702Row[] = [
    {
      num: "1",
      label: "Original contract sum",
      value: fmtMoney(g702?.originalContractSum),
    },
    {
      num: "1a",
      label: "SOV update / final contract",
      value: fmtMoney(g702?.sovUpdate),
    },
    {
      num: "2",
      label: "Net change by change orders",
      value: fmtMoney(g702?.netChangeByChangeOrders),
    },
    {
      num: "3",
      label: "Contract sum to date (1+1a+2)",
      value: fmtMoney(g702?.contractSumToDate),
      tone: "blue",
    },
    {
      num: "4a",
      label: "Total work completed to date",
      value: fmtMoney(g702?.totalWorkCompletedToDate),
    },
    {
      num: "4b",
      label: "Materials presently stored (Col F)",
      value: fmtMoney(g702?.materialsPresentlyStored),
      tone: "amber",
    },
    {
      num: "4",
      label: "Total completed & stored to date",
      value: fmtMoney(g702?.totalCompletedAndStored),
    },
    {
      num: "5",
      label: `Retainage${retainagePct != null ? ` — ${retainagePct}% of completed work` : ""}`,
      value: fmtMoney(g702?.totalRetainage),
      tone: "red",
    },
    {
      num: "6",
      label: "Total earned less retainage (4−5)",
      value: fmtMoney(g702?.totalEarnedLessRetainage),
    },
    {
      num: "7",
      label: "Less previous certificates for payment",
      value: fmtMoney(g702?.lessPreviousCertificates),
    },
    {
      num: "8",
      label: "Current payment due (6−7)",
      value: fmtMoney(g702?.currentPaymentDue),
      tone: "green",
      isTotal: true,
    },
    {
      num: "9",
      label: "Balance to finish including retainage",
      value: fmtMoney(g702?.balanceToFinish),
      tone: "amber",
    },
  ];

  return (
    <>
      <div className="sectionTitle">
        AIA G702 — Application &amp; Certificate for Payment
      </div>
      <div className="sectionSub">
        Application #
        {drawDetail ? String(drawDetail.draw.drawNumber).padStart(2, "0") : "—"}
        {drawDetail?.draw.toDate
          ? ` · Period to ${formatDate(drawDetail.draw.toDate, false)}`
          : ""}
      </div>

      <Card
        title="Application Summary"
        action={
          <Button
            type="button"
            className="btn secondary sm"
            onClick={onExportG702}
            disabled={exportingG702}
          >
            {exportingG702 ? "Generating…" : "📄 G702 PDF"}
          </Button>
        }
      >
        {loading ? (
          <div className="g702SkelWrap">
            {Array.from({ length: 10 }).map((_, i) => (
              <Skeleton key={i} height={16} className="g702SkelRow" />
            ))}
          </div>
        ) : (
          <table className="g702Table">
            <tbody>
              {rows.map((row) => (
                <tr key={row.num} className={row.isTotal ? "totalRow" : ""}>
                  <td className="gNum">{row.num}</td>
                  <td className="gLabel">{row.label}</td>
                  <td className={`gVal ${row.tone ?? ""}`}>{row.value}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>
    </>
  );
};

export default SummaryTab;
